import { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { Outlet } from "react-router";
import Sidebar from "./components/Sidebar.jsx";
import Header from "./components/Header.jsx";
import { sidebarWidths } from "@accredo/fe-shared/theme/index.js";
import { useSnackbar } from "notistack";
import { useSetAtom } from "jotai";
import { notificationAtom } from "../atoms/notificationAtom.js";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { getEnv } from "@accredo/fe-shared/env.js";
import Footer from "@accredo/fe-shared/components/Footer.jsx";
import Breadcrumbs from "@accredo/fe-shared/components/Breadcrumbs.jsx";
import EnvironmentBanner from "@accredo/fe-shared/components/EnvironmentBanner.jsx";
import MaintenanceNotice from "@accredo/fe-shared/components/MaintenanceNotice.jsx";
import logo_src from "@accredo/fe-shared/assets/logo.svg";
import ReleaseNoteModal from "../components/modals/ReleaseNoteModal.jsx";

const LayoutGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sidebarWidth",
})(({ theme, sidebarWidth }) => ({
  minHeight: "100vh",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  gridTemplateRows: "auto auto 1fr",
  gridTemplateAreas: '"banner" "header" "content"',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: `${sidebarWidth} minmax(0, 1fr)`,
    gridTemplateRows: "auto 1fr",
    gridTemplateAreas: '"banner banner" "sidebar main"',
    transition: "grid-template-columns 0.3s ease",
  },
}));

const MainColumn = styled(Box)(({ theme }) => ({
  gridArea: "main",
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    gridArea: "header / header / content / content",
  },
}));

const BannerWrapper = styled(Box)(({ theme }) => ({
  gridArea: "banner",
  position: "sticky",
  top: 0,
  zIndex: theme.zIndex.appBar + 1,
  width: "100%",
}));

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bannerHeight",
})(({ theme, bannerHeight = 0 }) => ({
  gridArea: "header",
  position: "sticky",
  top: bannerHeight,
  zIndex: theme.zIndex.appBar,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.up("md")]: {
    borderBottom: "none",
  },
}));

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bannerHeight",
})(({ theme, bannerHeight = 0 }) => ({
  gridArea: "sidebar",
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex",
    flexDirection: "column",
    position: "sticky",
    top: bannerHeight,
    height: "100vh",
    padding: theme.spacing(1),
    gap: theme.spacing(3),
    backgroundColor: theme.palette.background.sidebar,
    minWidth: sidebarWidths.minimized,
  },
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  gridArea: "content",
  flex: 1,
  padding: theme.spacing(2),
  width: "100%",
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(4),
    paddingTop: 0,
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(5),
    paddingTop: "0.5rem",
  },
}));

const ContentInner = styled(Box, {
  shouldForwardProp: (prop) => prop !== "maxContentWidth",
})(({ theme, maxContentWidth }) => ({
  width: "100%",
  margin: "0 auto",
  maxWidth: maxContentWidth || "80rem",
  overflowX: "visible",
}));

export default function BackendLayout({
  headerSlot,
  sidebarSlot,
  children,
  maxContentWidth,
}) {
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const setNotifications = useSetAtom(notificationAtom);

  const handleSidebarToggle = (minimized) => {
    setIsSidebarMinimized(minimized);
  };

  const sidebarWidth = isSidebarMinimized
    ? sidebarWidths.minimized
    : sidebarWidths.expanded;

  const app_env = getEnv("VITE_APP_ENV") || "production";
  const bannerHeight = app_env === "production" ? 0 : 32;

  useEffect(() => {
    let isActive = true;

    backendGet("data/notifications", {
      label: "Notification",
      options: {
        params: {
          sort: "-created_at",
          filter: { seen: false },
        },
      },
      setResult: (response) => {
        if (isActive) {
          setNotifications(response?.data || []);
        }
      },
      enqueueSnackbar: enqueueSnackbar,
    });

    return () => {
      isActive = false;
    };
  }, [enqueueSnackbar, setNotifications]);

  const headerContent = headerSlot ?? <Header />;
  const sidebarContent = sidebarSlot ?? (
    <Sidebar logoSrc={logo_src} onToggle={handleSidebarToggle} />
  );
  const content = children ?? <Outlet />;

  return (
    <>
      <ReleaseNoteModal />
      <LayoutGrid sidebarWidth={sidebarWidth}>
        <BannerWrapper>
          <EnvironmentBanner />
        </BannerWrapper>
        <SidebarWrapper bannerHeight={bannerHeight}>
          {sidebarContent}
        </SidebarWrapper>
        <MainColumn>
          <HeaderWrapper bannerHeight={bannerHeight}>
            {headerContent}
          </HeaderWrapper>
          <ContentWrapper>
            <ContentInner maxContentWidth={maxContentWidth}>
              <MaintenanceNotice sx={{ mb: 2 }} />
              <Box sx={{ display: { md: "none" }, mb: "20px" }}>
                <Breadcrumbs />
              </Box>

              {content}
            </ContentInner>
          </ContentWrapper>
          <Footer />
        </MainColumn>
      </LayoutGrid>
    </>
  );
}

BackendLayout.propTypes = {
  headerSlot: PropTypes.node,
  sidebarSlot: PropTypes.node,
  children: PropTypes.node,
  maxContentWidth: PropTypes.string,
};
